import { getAreaPointsProperty, getOpeningRoomIdsProperty, getRoomPolygonProperty, getRoomBaseProperty } from '@pointorama/potree-shared';

/** Fixed outer-edge extrusion (m) used when no floor outline face is available. */
export const OUTER_OPENING_WIDTH = 0.3;

/** Max angle between a candidate wall-face edge and the opening direction. */
const EDGE_PARALLEL_TOL_DEG = 20;
const EDGE_PARALLEL_TOL_SIN = Math.sin((EDGE_PARALLEL_TOL_DEG * Math.PI) / 180);

/** Max perpendicular distance (m) from the opening line to an accepted face. */
const WALL_FACE_MAX_DIST = 3;

/** Min fraction of the opening width a face must span (rest may overhang). */
const MIN_EDGE_OVERLAP_RATIO = 0.5;

/** Perpendicular distance (m) within which two parallel edges are the same face. */
const FACE_MERGE_TOL = 0.02;

const EPS = 1e-4;
/** Max perpendicular distance (m) a deduped XY vertex may sit off the A↔B line. */
const COLLINEAR_TOL = 0.01;

type XY = { x: number; y: number };
type XYZ = { x: number; y: number; z: number };

type RectResult = { fallback: false; corners: XYZ[] } | { fallback: true; reason: string };

/** A wall face: a point on it, its direction, and its signed offset from the opening line. */
type Edge = { p0: XY; dir: XY; offset: number };

function dedupeXY(points: XY[]): XY[] {
  const out: XY[] = [];
  for (const p of points) {
    if (!out.some((q) => Math.abs(q.x - p.x) < EPS && Math.abs(q.y - p.y) < EPS)) {
      out.push(p);
    }
  }
  return out;
}

/** The pair of points farthest apart in XY — the opening's two jamb anchors. */
function findExtents(points: XY[]): [XY, XY] | null {
  if (points.length < 2) return null;
  let best: [XY, XY] | null = null;
  let bestDistSq = -1;
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      const dx = points[j].x - points[i].x;
      const dy = points[j].y - points[i].y;
      const d = dx * dx + dy * dy;
      if (d > bestDistSq) {
        bestDistSq = d;
        best = [points[i], points[j]];
      }
    }
  }
  return best;
}

/** True iff every point lies within `tol` of the infinite line through A and B. */
function pointsAreCollinear(points: XY[], A: XY, B: XY, tol: number): boolean {
  const dx = B.x - A.x;
  const dy = B.y - A.y;
  const len = Math.hypot(dx, dy);
  if (len < EPS) return false;
  for (const p of points) {
    const cross = Math.abs(dx * (p.y - A.y) - dy * (p.x - A.x));
    if (cross / len > tol) return false;
  }
  return true;
}

function perp(v: XY): XY {
  return { x: -v.y, y: v.x };
}

function normalize(v: XY): XY {
  const len = Math.hypot(v.x, v.y);
  if (len < EPS) return { x: 0, y: 0 };
  return { x: v.x / len, y: v.y / len };
}

function dot(a: XY, b: XY): number {
  return a.x * b.x + a.y * b.y;
}

function centroidXY(poly: XY[]): XY {
  let sx = 0;
  let sy = 0;
  for (const p of poly) {
    sx += p.x;
    sy += p.y;
  }
  return { x: sx / poly.length, y: sy / poly.length };
}

function pointInPolygon(pt: XY, ring: XY[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i].x;
    const yi = ring[i].y;
    const xj = ring[j].x;
    const yj = ring[j].y;
    if (yi > pt.y !== yj > pt.y && pt.x < ((xj - xi) * (pt.y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Which side of the opening line (sign along `wallNormal`) the room sits on. */
function roomSide(wallMid: XY, wallNormal: XY, poly: XY[]): number {
  const probe = 0.01;
  if (pointInPolygon({ x: wallMid.x + wallNormal.x * probe, y: wallMid.y + wallNormal.y * probe }, poly)) return 1;
  if (pointInPolygon({ x: wallMid.x - wallNormal.x * probe, y: wallMid.y - wallNormal.y * probe }, poly)) return -1;
  const c = centroidXY(poly);
  return Math.sign(dot({ x: c.x - wallMid.x, y: c.y - wallMid.y }, wallNormal)) || 1;
}

function edgeParallelToWall(edgeDir: XY, wallDir: XY): boolean {
  const e = normalize(edgeDir);
  if (e.x === 0 && e.y === 0) return false;
  return Math.abs(e.x * wallDir.y - e.y * wallDir.x) <= EDGE_PARALLEL_TOL_SIN;
}

/** Overlap length of intervals [a0,a1] and [b0,b1] (negative when apart). */
function intervalOverlap(a0: number, a1: number, b0: number, b1: number): number {
  return Math.min(a1, b1) - Math.max(a0, b0);
}

/**
 * The wall face an opening pierces: among polygon edges that are parallel to the
 * opening, within WALL_FACE_MAX_DIST, and on `requiredSide` (0 = either),
 * collinear edges merge into one face and their span overlaps sum. A face needs
 * MIN_EDGE_OVERLAP_RATIO of the opening width; the nearest qualifier wins.
 */
function findWallFaceEdge(poly: XY[], wallMid: XY, wallDir: XY, wallNormal: XY, spanLo: number, spanHi: number, requiredSide: number): Edge | null {
  type Face = { offset: number; p0: XY; dir: XY; overlap: number };
  const faces: Face[] = [];

  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    const dir = { x: b.x - a.x, y: b.y - a.y };
    if (!edgeParallelToWall(dir, wallDir)) continue;

    const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    const offset = dot({ x: mid.x - wallMid.x, y: mid.y - wallMid.y }, wallNormal);
    if (Math.abs(offset) > WALL_FACE_MAX_DIST) continue;
    if (requiredSide !== 0 && Math.abs(offset) > EPS && Math.sign(offset) !== requiredSide) continue;

    const ea = dot(a, wallDir);
    const eb = dot(b, wallDir);
    const overlap = Math.max(0, intervalOverlap(Math.min(ea, eb), Math.max(ea, eb), spanLo, spanHi));

    const face = faces.find((g) => Math.abs(g.offset - offset) <= FACE_MERGE_TOL);
    if (face) face.overlap += overlap;
    else faces.push({ offset, p0: a, dir, overlap });
  }

  const minOverlap = MIN_EDGE_OVERLAP_RATIO * (spanHi - spanLo);
  let best: Face | null = null;
  for (const f of faces) {
    if (f.overlap < minOverlap) continue;
    if (!best || Math.abs(f.offset) < Math.abs(best.offset)) best = f;
  }
  return best ? { p0: best.p0, dir: best.dir, offset: best.offset } : null;
}

/** Jamb line (through `anchor`, along `wallNormal`) met with the face's infinite line. */
function projectJambOntoEdge(anchor: XY, wallNormal: XY, edge: Edge): XY | null {
  const denom = wallNormal.x * edge.dir.y - wallNormal.y * edge.dir.x;
  if (Math.abs(denom) < EPS) return null;
  const dx = edge.p0.x - anchor.x;
  const dy = edge.p0.y - anchor.y;
  const u = (dx * edge.dir.y - dy * edge.dir.x) / denom;
  return { x: anchor.x + wallNormal.x * u, y: anchor.y + wallNormal.y * u };
}

/**
 * `outlineRings` is the floor's resolved outline as a FLAT ring list (outer
 * rings + cavities alike) — callers resolve it via
 * `resolveFloorOutlineRings` / `resolveOutlineRingsFromAnnotations` +
 * `flattenRingComponents` (merged wall-sketch outline first, legacy OUTLINE
 * annotation fallback).
 */
export function computeOpeningRectangle(openingId: string, outlineRings?: XY[][]): RectResult {
  const roomIds = getOpeningRoomIdsProperty(openingId).value;
  if (!roomIds || roomIds.length === 0) {
    return { fallback: true, reason: 'no linked rooms' };
  }

  const rawPoints = getAreaPointsProperty(openingId).value;
  if (!rawPoints || rawPoints.length < 2) {
    return { fallback: true, reason: 'opening has no geometry' };
  }

  const xy = dedupeXY(rawPoints.map((p) => ({ x: p.position.x, y: p.position.y })));
  const extents = findExtents(xy);
  if (!extents) {
    return { fallback: true, reason: 'opening collapses to a single XY point' };
  }
  // Footprint must be a line on the wall — extra collinear points (arches,
  // segments) are fine; an off-line point means a malformed opening.
  if (!pointsAreCollinear(xy, extents[0], extents[1], COLLINEAR_TOL)) {
    return { fallback: true, reason: 'opening XY footprint is not collinear' };
  }
  const [A, B] = extents;
  const wallDir = normalize({ x: B.x - A.x, y: B.y - A.y });
  const wallNormal = perp(wallDir);
  const wallMid: XY = { x: (A.x + B.x) / 2, y: (A.y + B.y) / 2 };

  // Opening extent along the wall — the lateral-overlap window for face matching.
  const eA = dot(A, wallDir);
  const eB = dot(B, wallDir);
  const spanLo = Math.min(eA, eB);
  const spanHi = Math.max(eA, eB);

  const rooms = roomIds
    .map((id) => {
      const polygon = getRoomPolygonProperty(id).value;
      if (!polygon || polygon.length < 3) return null;
      const polyXY = polygon.map((p) => ({ x: p.x, y: p.y }));
      const side = roomSide(wallMid, wallNormal, polyXY);
      const baseZ = getRoomBaseProperty(id).value.position.z;
      return { id, polyXY, side, baseZ };
    })
    .filter((r): r is { id: string; polyXY: XY[]; side: number; baseZ: number } => r !== null);

  if (rooms.length === 0) {
    return { fallback: true, reason: 'linked rooms have no polygon' };
  }

  // Project both jambs onto a face's infinite line → its two corners.
  function corners(edge: Edge): [XY, XY] | null {
    const a = projectJambOntoEdge(A, wallNormal, edge);
    const b = projectJambOntoEdge(B, wallNormal, edge);
    return a && b ? [a, b] : null;
  }

  const z = rooms[0].baseZ;

  if (rooms.length >= 2) {
    // Interior opening: each room's pierced face is on its own side of the wall,
    // so the two outermost faces are the door's long sides.
    const faces = rooms.map((r) => findWallFaceEdge(r.polyXY, wallMid, wallDir, wallNormal, spanLo, spanHi, 0)).filter((e): e is Edge => e !== null);
    if (faces.length < 2) {
      return { fallback: true, reason: 'no wall face found for a linked room' };
    }
    faces.sort((a, b) => a.offset - b.offset);
    const faceLow = faces[0];
    const faceHigh = faces[faces.length - 1];
    if (Math.abs(faceHigh.offset - faceLow.offset) < EPS) {
      return { fallback: true, reason: 'linked room faces are coincident' };
    }
    const lowPair = corners(faceLow);
    const highPair = corners(faceHigh);
    if (!lowPair || !highPair) {
      return { fallback: true, reason: 'jamb projection onto wall face failed' };
    }
    const [aL, bL] = lowPair;
    const [aR, bR] = highPair;
    return {
      fallback: false,
      corners: [
        { x: aL.x, y: aL.y, z },
        { x: bL.x, y: bL.y, z },
        { x: bR.x, y: bR.y, z },
        { x: aR.x, y: aR.y, z },
      ],
    };
  }

  // Exterior opening: inner face from the room, outer face from the floor
  // outline. Inner falls back to the raw anchors, outer to a fixed extrusion.
  const room = rooms[0];
  const innerEdge = findWallFaceEdge(room.polyXY, wallMid, wallDir, wallNormal, spanLo, spanHi, 0);
  const innerPair = innerEdge && corners(innerEdge);
  const aInner: XY = innerPair ? innerPair[0] : A;
  const bInner: XY = innerPair ? innerPair[1] : B;
  const outwardSign = -room.side;

  let outerEdge: Edge | null = null;
  for (const ring of outlineRings ?? []) {
    const e = findWallFaceEdge(ring, wallMid, wallDir, wallNormal, spanLo, spanHi, outwardSign);
    if (e && (!outerEdge || Math.abs(e.offset) < Math.abs(outerEdge.offset))) outerEdge = e;
  }
  const outerPair = outerEdge && corners(outerEdge);

  const aOuter: XY = outerPair ? outerPair[0] : { x: aInner.x + wallNormal.x * OUTER_OPENING_WIDTH * outwardSign, y: aInner.y + wallNormal.y * OUTER_OPENING_WIDTH * outwardSign };
  const bOuter: XY = outerPair ? outerPair[1] : { x: bInner.x + wallNormal.x * OUTER_OPENING_WIDTH * outwardSign, y: bInner.y + wallNormal.y * OUTER_OPENING_WIDTH * outwardSign };

  return {
    fallback: false,
    corners: [
      { x: aInner.x, y: aInner.y, z },
      { x: bInner.x, y: bInner.y, z },
      { x: bOuter.x, y: bOuter.y, z },
      { x: aOuter.x, y: aOuter.y, z },
    ],
  };
}
